import type { IconType } from "@clipboard-health/ui-components";
import {
  BottomSheetWithIntent,
  Text,
  TextArea,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import type { IntentVariant } from "@clipboard-health/ui-theme";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { TIMEKEEPING_REASON_CONSTANTS } from "./constants";

type BottomSheetIntentType = "info" | "error" | "success";

interface BottomSheetIntentProps {
  intent: IntentVariant;
  intentIcon: IconType;
}

export function getBottomSheetIntent(type: BottomSheetIntentType): BottomSheetIntentProps {
  switch (type) {
    case "info":
      return { intent: "neutral", intentIcon: "info" };
    case "error":
      return { intent: "neutral", intentIcon: "triangle-exclamation" };
    case "success":
      return { intent: "neutral", intentIcon: "checkmark" };
  }
}

type Step = "picker" | "acknowledgement" | "reason";

export interface TimekeepingAdjustmentBottomSheetProps {
  modalState: UseModalState;
  // Intent banner (computed by caller)
  intent?: IntentVariant;
  intentContent?: ReactNode;
  intentIcon?: IconType;
  // Picker step
  pickerTitle: string;
  hasChanges: boolean;
  isPickerScrolling: boolean;
  isPickerValid: boolean;
  requiresApproval: boolean;
  /**
   * Called when the user taps Next/Save on the picker step.
   * Return false to block step advancement (e.g. show a validation error).
   * Omit to always allow advancement when the button is enabled.
   */
  onPickerConfirm?: () => boolean;
  children: ReactNode;
  // Optional middle step between picker and reason (e.g. break policy acknowledgement)
  middleStepContent?: ReactNode;
  isMiddleStepComplete?: boolean;
  // Reason step
  reasonTitle: string;
  existingReason?: string;
  /** Helper text shown beneath the reason title, e.g. when the user is editing an existing reason. */
  reasonHelperText?: string;
  // Callbacks
  onConfirm: (reason: string) => void;
  onCancel?: () => void;
}

export function TimekeepingAdjustmentBottomSheet(props: TimekeepingAdjustmentBottomSheetProps) {
  const {
    modalState,
    intent,
    intentContent,
    intentIcon,
    pickerTitle,
    hasChanges,
    isPickerScrolling,
    isPickerValid,
    requiresApproval,
    onPickerConfirm,
    children,
    middleStepContent,
    isMiddleStepComplete = false,
    reasonTitle,
    existingReason,
    reasonHelperText,
    onConfirm,
    onCancel,
  } = props;

  const [step, setStep] = useState<Step>("picker");
  const [reason, setReason] = useState(existingReason ?? "");
  const isEditing = isDefined(existingReason) && existingReason.length > 0;

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setStep("picker");
      setReason(existingReason ?? "");
    }
  }, [modalState.modalIsOpen, existingReason]);

  const handlePickerNext = () => {
    const canProceed = onPickerConfirm?.() ?? true;
    if (!canProceed) {
      return;
    }

    if (!requiresApproval) {
      onConfirm("");
      modalState.closeModal();
    } else if (isDefined(middleStepContent)) {
      setStep("acknowledgement");
    } else {
      setStep("reason");
    }
  };

  const handleReasonConfirm = () => {
    onConfirm(reason);
    modalState.closeModal();
  };

  const handleCancel = () => {
    onCancel?.();
    modalState.closeModal();
  };

  const cancelButton = (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Button
        variant="text"
        size="small"
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 0,
          height: "1.6875rem",
        }}
        onClick={handleCancel}
      >
        Cancel
      </Button>
    </Box>
  );

  function renderFooter() {
    if (step === "picker") {
      return (
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isPickerValid || !hasChanges || isPickerScrolling}
            onClick={handlePickerNext}
          >
            {requiresApproval ? "Next" : "Save"}
          </Button>
          {cancelButton}
        </DialogFooter>
      );
    }

    if (step === "acknowledgement") {
      return (
        <DialogFooter orientation="vertical">
          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!isMiddleStepComplete}
            onClick={() => setStep("reason")}
          >
            Continue
          </Button>
          {cancelButton}
        </DialogFooter>
      );
    }

    return (
      <DialogFooter orientation="vertical">
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={reason.length < TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS}
          onClick={handleReasonConfirm}
        >
          Update timesheet
        </Button>
        {cancelButton}
      </DialogFooter>
    );
  }

  return (
    <BottomSheetWithIntent
      modalState={modalState}
      intent={intent}
      intentContent={intentContent}
      intentIcon={intentIcon}
      footer={renderFooter()}
    >
      <DialogContent>
        {step === "picker" && (
          <Stack spacing={6}>
            <Title semibold variant="h4" component="h2">
              {pickerTitle}
            </Title>
            {children}
          </Stack>
        )}

        {step === "acknowledgement" && (
          <Stack spacing={6}>
            <IconButton iconType="chevron-left" variant="ghost" onClick={() => setStep("picker")} />
            {middleStepContent}
          </Stack>
        )}

        {step === "reason" && (
          <Stack spacing={6}>
            <IconButton
              iconType="chevron-left"
              variant="ghost"
              onClick={() => setStep(isDefined(middleStepContent) ? "acknowledgement" : "picker")}
            />
            <Title semibold variant="h4" component="h2">
              {reasonTitle}
            </Title>
            {isEditing && isDefined(reasonHelperText) && (
              <Text variant="body2">{reasonHelperText}</Text>
            )}
            <TextArea
              fullWidth
              required
              value={reason}
              error={
                reason.length > 0 && reason.length < TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS
              }
              sx={(theme) => ({
                height: theme.spacing(12),
                alignItems: "flex-start",
                "& textarea": { overflowY: "auto !important" },
              })}
              inputProps={{ maxLength: TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS }}
              placeholder="Let the workplace know why you adjusted your time."
              onChange={(event) =>
                setReason(event.target.value.slice(0, TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS))
              }
            />
            {reason.length > 0 && (
              <Text
                variant="caption"
                sx={{
                  color: (theme) =>
                    reason.length < TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS
                      ? theme.palette.error.main
                      : theme.palette.text.secondary,
                }}
              >
                {reason.length < TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS
                  ? `Minimum ${TIMEKEEPING_REASON_CONSTANTS.MIN_CHARACTERS} characters required`
                  : `${reason.length}/${TIMEKEEPING_REASON_CONSTANTS.MAX_CHARACTERS} characters`}
              </Text>
            )}
          </Stack>
        )}
      </DialogContent>
    </BottomSheetWithIntent>
  );
}
